/**
 * EquipmentTypeDocsTab — STORY-1306 slice D (proto #13 docs-tab).
 *
 * Read-only dokumentum-lista a #407 Document-reuse kontraktusra
 * (`equipmentTypeDocumentsReadSchema`, kind: MANUAL/SAFETY/WARRANTY/
 * CERTIFICATE/OTHER). A név új lapon nyitja a fájlt.
 *
 * DIVERGENCIA-NAPLÓ: a mockup „Új dokumentum" upload-CTA-ja NINCS — az upload
 * külön slice (a seam read-only, Zeno-ratify). A docs a Document-reuse mintán
 * él (nincs dedikált tábla, BE-1 handover-fotó precedens).
 */

import { useEquipmentTypeDocuments } from '@/hooks/use-equipment-type-documents';
import type { EquipmentTypeDocumentKind } from '@kgc/equipment-type-domain';
import { useTranslation } from '@kgc/i18n';

const KIND_BADGE_CLASSES: Record<EquipmentTypeDocumentKind, string> = {
  MANUAL: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  SAFETY: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  WARRANTY: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  CERTIFICATE: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  OTHER: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

/**
 * Scheme-guard a doc-linkre: a kontraktus `url`-je sima `z.string()` →
 * egy `javascript:` sémájú érték XSS lenne href-ként. Csak http(s) megy át,
 * minden más '#'. (Seam-szintű fix — https-only refine, mint a `httpsImageUrl`
 * — Zenónál jelezve; ez a guard defense-in-depth marad.)
 */
function safeHref(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? url : '#';
  } catch {
    return '#';
  }
}

/** Bájt → human-readable (kB/MB, 1 tizedes). */
function formatSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} kB`;
  return `${bytes} B`;
}

interface EquipmentTypeDocsTabProps {
  typeId: string;
}

export function EquipmentTypeDocsTab({ typeId }: EquipmentTypeDocsTabProps) {
  const { t } = useTranslation();
  const { documents, isLoading, isError } = useEquipmentTypeDocuments(typeId);

  if (isLoading) {
    return (
      <p data-testid="docs-tab-loading" className="py-6 text-sm text-muted-foreground">
        {t('equipment.types.editor.docs.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <p data-testid="docs-tab-error" className="py-6 text-sm text-rose-600 dark:text-rose-400">
        {t('equipment.types.editor.docs.error')}
      </p>
    );
  }

  if (documents.length === 0) {
    return (
      <p data-testid="docs-tab-empty" className="py-6 text-sm text-muted-foreground">
        {t('equipment.types.editor.docs.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        <span data-testid="docs-tab-count">{documents.length}</span>{' '}
        {t('equipment.types.editor.docs.countSuffix')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-4">{t('equipment.types.editor.docs.name')}</th>
              <th className="py-2 pr-4">{t('equipment.types.editor.docs.kind')}</th>
              <th className="py-2 pr-4">{t('equipment.types.editor.docs.size')}</th>
              <th className="py-2">{t('equipment.types.editor.docs.uploadedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {documents.map(doc => (
              <tr
                key={doc.id}
                className="border-b border-gray-100 dark:border-gray-800"
                data-testid={`docs-tab-row-${doc.id}`}
              >
                <td className="py-2 pr-4">
                  <a
                    href={safeHref(doc.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary hover:underline"
                    data-testid={`docs-tab-open-${doc.id}`}
                  >
                    {doc.name}
                  </a>
                </td>
                <td className="py-2 pr-4">
                  <span
                    className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ${KIND_BADGE_CLASSES[doc.kind]}`}
                    data-testid={`docs-kind-${doc.kind}`}
                  >
                    {t(`equipment.types.editor.docs.kinds.${doc.kind}`)}
                  </span>
                </td>
                <td className="py-2 pr-4 text-muted-foreground" data-testid={`docs-size-${doc.id}`}>
                  {formatSize(doc.sizeBytes)}
                </td>
                <td className="py-2 text-muted-foreground">
                  {doc.uploadedAt.toLocaleDateString('hu-HU')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
